import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { CalendarClock, Clock, LayoutGrid, Printer } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatDate } from '@/lib/date'
import { useAdminDepartments } from '@/hooks/useAdminDepartments'
import type { Job, Paginated } from '@/types'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import Badge, { StateBadge } from '@/components/ui/Badge'
import { Table, Row, Cell, EmptyRow } from '@/components/ui/Table'
import LogoLoader from '@/components/ui/LogoLoader'
import Pager from '@/components/ui/Pager'
import UrgentBadge from '@/components/job/UrgentBadge'
import { RiskBadge } from '@/components/admin/ai/AiBadges'
import { useLateRisks } from '@/lib/aiInsights'
import { aiFeatures } from '@/lib/aiFeatures'

/**
 * Every production job. Filter to the jobs currently at one section, or to
 * late ones - a job whose current section is past its booked day. Filters and
 * page live in the URL so the dashboard's "Late jobs" tile links straight in.
 * Orders still pending in the office are proceeded from Orders > Pending; here
 * they only carry a Pending chip.
 */
export default function AdminJobs() {
  const [params, setParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const late = params.get('late') === '1'
  const departmentId = params.get('department_id') ?? ''
  const page = Number(params.get('page') ?? 1) || 1

  const update = (next: Record<string, string | null>) => {
    const merged = new URLSearchParams(params)
    for (const [key, value] of Object.entries(next)) {
      if (value === null || value === '') merged.delete(key)
      else merged.set(key, value)
    }
    // A changed filter starts again from the first page.
    if (!('page' in next)) merged.delete('page')
    setParams(merged, { replace: true })
  }

  const departments = useAdminDepartments()
  // Likely to miss the delivery date at the pace sections really work; the
  // reason is the badge's hover text. Overdue is left out here: the red date
  // already says it (client 2026-10-28), so only the early warnings show.
  const risks = useLateRisks(aiFeatures.jobRisk)
  const earlyRisk = (jobId: number) => {
    const risk = risks.data?.get(jobId)
    return risk && risk.level !== 'overdue' ? risk : null
  }

  const { data, isLoading } = useQuery({
    queryKey: ['admin-jobs', search, late, departmentId, page],
    queryFn: async () =>
      (
        await api.get<Paginated<Job>>('/admin/jobs', {
          params: {
            search: search || undefined,
            late: late ? 1 : undefined,
            department_id: departmentId || undefined,
            page,
          },
        })
      ).data,
  })

  const sectionOptions = [
    { value: '', label: 'All sections' },
    ...(departments.data?.departments ?? []).map((dept) => ({ value: String(dept.id), label: dept.name })),
  ]

  return (
    <div>
      <PageHeader
        title="Production jobs"
        subtitle={late ? 'Jobs whose current section is past its booked day' : 'Every order becomes a job with its own QR code'}
        actions={
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <Input
              placeholder="Search job code..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                if (page !== 1) update({})
              }}
              className="w-full sm:w-48"
            />
            <Select
              className="w-full sm:w-44"
              value={departmentId}
              onChange={(value) => update({ department_id: value })}
              options={sectionOptions}
              placeholder="All sections"
            />
            <Button
              variant="outline"
              aria-pressed={late}
              onClick={() => update({ late: late ? null : '1' })}
              className={cn(late && 'border-danger/60 bg-danger/20 text-danger hover:bg-danger/10')}
            >
              <CalendarClock className="size-4" /> Late only
            </Button>
            <Link to="/admin/settings/capacity?tab=sections">
              <Button variant="outline">
                <LayoutGrid className="size-4" /> Section load
              </Button>
            </Link>
          </div>
        }
      />

      {isLoading ? (
        <LogoLoader />
      ) : (
        <>
          <Table
            columns={['Job code', 'Order', 'Customer', 'Status', 'Current section', 'Due at section', 'Location', 'Job sheet']}
          >
            {(data?.data.length ?? 0) === 0 && (
              <EmptyRow colSpan={8}>{late ? 'No late jobs - everything is on schedule.' : undefined}</EmptyRow>
            )}
            {data?.data.map((job) => (
              <Row key={job.id} onClick={() => window.open(`/admin/jobs/${job.id}/sheet`, '_blank', 'noopener')}>
                <Cell className="font-mono text-xs font-bold">
                  <div className="flex flex-col items-start gap-1">
                    <span className="whitespace-nowrap">{job.display_code ?? job.code}</span>
                    <UrgentBadge urgent={job.order?.is_urgent} className="font-sans" />
                  </div>
                </Cell>
                <Cell>{job.order?.number}</Cell>
                <Cell>{job.order?.agent?.name}</Cell>
                <Cell>
                  <StateBadge state={job.status} />
                </Cell>
                <Cell>
                  <div className="flex flex-col items-start gap-1">
                    {job.current_department?.name ?? (job.status === 'completed' ? 'Done' : '-')}
                    {job.awaiting_proceed && (
                      <Badge tone="warning" size="sm">
                        <Clock className="size-3" /> Pending
                      </Badge>
                    )}
                  </div>
                </Cell>
                <Cell className={cn(job.late && 'font-semibold text-status-danger')}>
                  <div className="flex flex-col items-start gap-1">
                    <span className="whitespace-nowrap">
                      {job.current_department_due ? formatDate(job.current_department_due) : '-'}
                    </span>
                    {aiFeatures.jobRisk && <RiskBadge risk={earlyRisk(job.id)} size="sm" />}
                  </div>
                </Cell>
                <Cell>{job.warehouse ? `External: ${job.warehouse.name}` : 'In-house'}</Cell>
                <Cell>
                  <Link to={`/admin/jobs/${job.id}/sheet`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                    <Button variant="outline">
                      <Printer className="size-4" /> Sheet
                    </Button>
                  </Link>
                </Cell>
              </Row>
            ))}
          </Table>
          <Pager meta={data?.meta} page={page} onPage={(next) => update({ page: String(next) })} />
        </>
      )}
    </div>
  )
}
